import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button, Card, Input, Modal, PageHeader, Table, Td, Th } from "@/components/ui";
import { confirmPopup } from "@/components/popup";
import { CopyField } from "@/components/CopyField";

interface ReferralUser {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  total_refs: number;
  paid_refs: number;
  conversion_pct: number;
  active_7d: number;
  payments: number;
  revenue: number;
  avg_check: number;
  earned: number;
  new_count: number;
  new_revenue: number;
  renewal_count: number;
  renewal_revenue: number;
  percent: number | null;
  balance: number;
}

interface ReferralStatsResponse {
  items: ReferralUser[];
  total: number;
}

interface TrackingLink {
  id: number;
  slug: string;
  name: string;
  clicks: number;
  signups: number;
  active_7d: number;
  payments: number;
  paid_signups: number;
  revenue: number;
  conversion: number;
  avg_check: number;
  percent: number | null;
  traffer_user_id: number | null;
  traffer_username: string | null;
  traffer_earned: number;
  new_count: number;
  new_revenue: number;
  renewal_count: number;
  renewal_revenue: number;
  created_at: string;
}

interface TrackingLinkListResponse {
  items: TrackingLink[];
}

interface UserHit {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
}

interface AttributedUser {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  created_at: string;
  payments: number;
  spent: number;
}

const rub0 = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});
const rub2 = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" });
const num = new Intl.NumberFormat("ru-RU");

function dt(s: string): string {
  const d = new Date(s);
  return isNaN(+d) ? "—" : d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function sanitizeSlug(s: string): string {
  return s.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "");
}

function convClass(conv: number): string {
  return conv >= 50 ? "text-emerald-400" : conv >= 20 ? "text-amber-400" : "text-zinc-400";
}

function userLabel(u: { username: string | null; first_name: string | null; id: number }): string {
  return u.username ? `@${u.username}` : u.first_name ?? `#${u.id}`;
}

function trackingUrl(botName: string, slug: string): string {
  return `https://t.me/${botName}?start=src_${slug}`;
}

function useBotUsername(): string {
  const q = useQuery<{ username: string }>({
    queryKey: ["bot-info"],
    queryFn: () => api("/api/admin/bot-info"),
    staleTime: Infinity,
  });
  return q.data?.username ?? "";
}

type Tab = "stats" | "links";

export default function Referrals() {
  const [tab, setTab] = useState<Tab>("links");

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <PageHeader title="Рефералы" />

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <TabButton active={tab === "links"} onClick={() => setTab("links")}>
          Трекинг-ссылки
        </TabButton>
        <TabButton active={tab === "stats"} onClick={() => setTab("stats")}>
          Реферальная статистика
        </TabButton>
      </div>

      {tab === "links" ? <TrackingLinksTab /> : <ReferralStatsTab />}
    </div>
  );
}

function TrackingLinksTab() {
  const qc = useQueryClient();
  const botName = useBotUsername();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const q = useQuery<TrackingLinkListResponse>({
    queryKey: ["tracking-links"],
    queryFn: () => api("/api/admin/tracking-links"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/admin/tracking-links/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tracking-links"] }),
  });

  const items = q.data?.items ?? [];
  const totals = items.reduce(
    (a, l) => {
      a.paid += l.paid_signups;
      a.active += l.active_7d;
      a.payments += l.payments;
      a.revenue += l.revenue;
      a.clicks += l.clicks;
      return a;
    },
    { paid: 0, active: 0, payments: 0, revenue: 0, clicks: 0 },
  );
  const detail = items.find((l) => l.id === detailId) ?? null;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Платящих" value={num.format(totals.paid)} hint="уник. оплативших по всем ссылкам" accent="emerald" />
        <KpiCard label="Активных 7д" value={num.format(totals.active)} hint="заходили в бот за 7 дней" />
        <KpiCard label="Оплат" value={num.format(totals.payments)} hint="успешных платежей всего" />
        <KpiCard label="Выручка" value={rub0.format(totals.revenue)} hint="сумма RUB-оплат по всем ссылкам" accent="emerald" />
      </div>

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-zinc-400 text-sm">
          ссылок: {items.length} · переходов: {num.format(totals.clicks)}
        </div>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          + Создать ссылку
        </Button>
      </div>

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {q.isError && <div className="text-red-400">Ошибка загрузки</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {items.map((l) => (
          <LinkCard
            key={l.id}
            link={l}
            botName={botName}
            onOpen={() => setDetailId(l.id)}
            onDelete={async () => {
              if (await confirmPopup({ title: "Удалить ссылку?", body: `«${l.name}» — статистика по ней пропадёт.`, danger: true, confirmLabel: "Удалить" })) remove.mutate(l.id);
            }}
          />
        ))}
      </div>

      {items.length === 0 && !q.isLoading && (
        <div className="text-zinc-500 text-sm mt-6 border border-dashed border-zinc-800 rounded-xl p-8 text-center">
          Ссылок пока нет. Нажми «Создать ссылку», чтобы завести первую.
        </div>
      )}

      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} botName={botName} />
      <DetailModal
        link={detail}
        botName={botName}
        onClose={() => setDetailId(null)}
        onDelete={async () => {
          if (detail && (await confirmPopup({ title: "Удалить ссылку?", body: `«${detail.name}»`, danger: true, confirmLabel: "Удалить" }))) {
            remove.mutate(detail.id);
            setDetailId(null);
          }
        }}
      />
    </>
  );
}

function LinkCard({
  link,
  botName,
  onOpen,
  onDelete,
}: {
  link: TrackingLink;
  botName: string;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const url = trackingUrl(botName, link.slug);

  return (
    <div
      onClick={onOpen}
      className="group rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex flex-col gap-3 cursor-pointer hover:border-zinc-600 hover:bg-zinc-900/70 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-zinc-100 font-medium truncate group-hover:text-brand transition-colors">
            {link.name}
          </div>
          <div className="text-xs text-zinc-600 font-mono">src_{link.slug}</div>
        </div>
        <div className="flex gap-1.5 shrink-0" onClick={stop}>
          <CopyField size="button-only" text={url} label="Копировать" />
          <Button variant="danger" onClick={onDelete}>
            Удалить
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 rounded-lg bg-zinc-950/40 px-3 py-2">
        <div className="text-sm min-w-0 truncate">
          <span className="text-zinc-500 text-xs">Траффер: </span>
          {link.traffer_user_id ? (
            <span className="text-zinc-200">
              <span className="text-brand">
                {link.traffer_username ? `@${link.traffer_username}` : `#${link.traffer_user_id}`}
              </span>
              <span className="text-zinc-500"> · {link.percent ?? 0}% · </span>
              <span className="text-amber-400">{rub0.format(link.traffer_earned)}</span>
            </span>
          ) : (
            <span className="text-zinc-600">не назначен</span>
          )}
        </div>
        <span className="text-xs text-zinc-600 group-hover:text-zinc-400 shrink-0">подробнее →</span>
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-8 gap-y-3 gap-x-2 pt-1">
        <Stat label="Переходы" value={num.format(link.clicks)} />
        <Stat label="Рег." value={num.format(link.signups)} />
        <Stat label="Актив 7д" value={num.format(link.active_7d)} />
        <Stat label="Оплат" value={num.format(link.payments)} />
        <Stat label="Платящих" value={num.format(link.paid_signups)} className="text-emerald-400" />
        <Stat label="Конв." value={`${link.conversion.toFixed(1)}%`} className={convClass(link.conversion)} />
        <Stat label="Ср. чек" value={link.avg_check ? rub0.format(link.avg_check) : "—"} />
        <Stat label="Выручка" value={rub0.format(link.revenue)} className="text-zinc-100" />
      </div>
      <NewRenewal nc={link.new_count} nr={link.new_revenue} rc={link.renewal_count} rr={link.renewal_revenue} />
    </div>
  );
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-600 mb-0.5">{label}</div>
      <div className={`text-sm font-medium ${className ?? "text-zinc-300"}`}>{value}</div>
    </div>
  );
}

function NewRenewal({ nc, nr, rc, rr }: { nc: number; nr: number; rc: number; rr: number }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1 border-t border-zinc-800/60 mt-1">
      <span className="text-zinc-500">
        🆕 Новые: <span className="text-emerald-400 font-medium">{num.format(nc)}</span> · {rub0.format(nr)}
      </span>
      <span className="text-zinc-500">
        🔁 Продления: <span className="text-sky-400 font-medium">{num.format(rc)}</span> · {rub0.format(rr)}
      </span>
    </div>
  );
}

function DetailModal({
  link,
  botName,
  onClose,
  onDelete,
}: {
  link: TrackingLink | null;
  botName: string;
  onClose: () => void;
  onDelete: () => void;
}) {
  const report = useQuery<{ report: string }>({
    queryKey: ["tracking-report", link?.id],
    queryFn: () => api(`/api/admin/tracking-links/${link!.id}/report`),
    enabled: !!link,
  });
  const users = useQuery<{ users: AttributedUser[] }>({
    queryKey: ["tracking-users", link?.id],
    queryFn: () => api(`/api/admin/tracking-links/${link!.id}/users`),
    enabled: !!link,
  });

  if (!link) return null;
  const url = trackingUrl(botName, link.slug);
  const userList = users.data?.users ?? [];

  return (
    <Modal open={link !== null} onClose={onClose} title={link.name} maxWidth="max-w-3xl">
      <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
        <div>
          <div className="text-xs text-zinc-500 mb-1">
            Ссылка · src_{link.slug} · создана {dt(link.created_at)}
          </div>
          <CopyField size="lg" text={url} />
        </div>

        <Card title="Воронка">
          <Funnel clicks={link.clicks} signups={link.signups} paid={link.paid_signups} />
        </Card>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-y-4 gap-x-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <Stat label="Переходы" value={num.format(link.clicks)} />
          <Stat label="Регистрации" value={num.format(link.signups)} />
          <Stat label="Активных 7д" value={num.format(link.active_7d)} />
          <Stat label="Оплат" value={num.format(link.payments)} />
          <Stat label="Платящих" value={num.format(link.paid_signups)} className="text-emerald-400" />
          <Stat label="Конверсия" value={`${link.conversion.toFixed(1)}%`} className={convClass(link.conversion)} />
          <Stat label="Средний чек" value={link.avg_check ? rub0.format(link.avg_check) : "—"} />
          <Stat label="Выручка" value={rub0.format(link.revenue)} className="text-zinc-100" />
          <Stat label="Трафферу" value={link.traffer_user_id ? rub0.format(link.traffer_earned) : "—"} className="text-amber-400" />
        </div>

        <Card title="Новые / продления">
          <NewRenewal nc={link.new_count} nr={link.new_revenue} rc={link.renewal_count} rr={link.renewal_revenue} />
        </Card>

        <Card title="Траффер и выплата">
          <TrafferEditor link={link} />
        </Card>

        <Card title={`Привлечённые пользователи · ${userList.length}`}>
          {users.isLoading && <div className="text-zinc-500 text-sm">Загрузка…</div>}
          {!users.isLoading && userList.length === 0 && (
            <div className="text-zinc-500 text-sm">Пока никого не привёл.</div>
          )}
          {userList.length > 0 && (
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>пользователь</Th>
                    <Th>пришёл</Th>
                    <Th>оплат</Th>
                    <Th>потратил</Th>
                    <Th>статус</Th>
                  </tr>
                </thead>
                <tbody>
                  {userList.map((u) => (
                    <tr key={u.id} className="hover:bg-zinc-900/50">
                      <Td>
                        <Link to={`/users/${u.id}`} className="text-brand hover:underline">
                          {userLabel(u)}
                        </Link>
                      </Td>
                      <Td className="text-zinc-400">{dt(u.created_at)}</Td>
                      <Td className="text-zinc-300">{u.payments}</Td>
                      <Td className="text-zinc-100">{u.spent ? rub0.format(u.spent) : "—"}</Td>
                      <Td>
                        {u.spent > 0 ? (
                          <span className="text-emerald-400 text-xs">платящий</span>
                        ) : (
                          <span className="text-zinc-600 text-xs">лид</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

        <Card title="Отчёт для партнёра">
          {report.isLoading && <div className="text-zinc-500 text-sm">Загрузка…</div>}
          {report.data?.report && (
            <>
              <pre className="whitespace-pre-wrap text-sm text-zinc-200 bg-zinc-950/60 border border-zinc-800 rounded-lg p-4 font-sans mb-3">
                {report.data.report}
              </pre>
              <CopyField size="button-only" text={report.data.report} label="Скопировать отчёт" />
            </>
          )}
        </Card>

        <div className="flex justify-between pt-1">
          <Button variant="danger" onClick={onDelete}>
            Удалить ссылку
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Funnel({ clicks, signups, paid }: { clicks: number; signups: number; paid: number }) {
  const stages = [
    { label: "Переходы", value: clicks, color: "bg-zinc-500" },
    { label: "Регистрации", value: signups, color: "bg-sky-500" },
    { label: "Платящих", value: paid, color: "bg-emerald-500" },
  ];
  const max = Math.max(clicks, 1);
  return (
    <div className="space-y-3">
      {stages.map((s, i) => {
        const prev = i > 0 ? stages[i - 1].value : null;
        const dropPct = prev && prev > 0 ? Math.round((s.value / prev) * 100) : null;
        const width = Math.round((s.value / max) * 100);
        return (
          <div key={s.label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-zinc-400">{s.label}</span>
              <span className="text-zinc-200">
                {num.format(s.value)}
                {dropPct != null && <span className="text-zinc-500"> · {dropPct}% ↓</span>}
              </span>
            </div>
            <div className="h-2.5 rounded bg-zinc-800 overflow-hidden">
              <div className={`h-full ${s.color}`} style={{ width: `${Math.max(width, 2)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Traffer search-and-pick by @username. */
function TrafferPicker({
  value,
  onChange,
}: {
  value: { id: number; label: string } | null;
  onChange: (v: { id: number; label: string } | null) => void;
}) {
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const q = useQuery<{ users: UserHit[] }>({
    queryKey: ["user-search", debounced],
    queryFn: () => api(`/api/admin/users/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2 && !value,
  });

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2">
        <span className="text-sm text-zinc-200 flex-1 truncate">{value.label}</span>
        <button
          className="text-xs text-zinc-500 hover:text-red-400"
          onClick={() => {
            onChange(null);
            setTerm("");
          }}
        >
          ✕ убрать
        </button>
      </div>
    );
  }

  const hits = q.data?.users ?? [];

  return (
    <div className="relative">
      <Input
        value={term}
        placeholder="@username или telegram id"
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && debounced.length >= 2 && (
        <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-900 shadow-xl">
          {q.isLoading && <div className="px-3 py-2 text-xs text-zinc-500">Поиск…</div>}
          {!q.isLoading && hits.length === 0 && (
            <div className="px-3 py-2 text-xs text-zinc-500">Никого не найдено</div>
          )}
          {hits.map((u) => (
            <button
              key={u.id}
              className="w-full text-left px-3 py-2 hover:bg-zinc-800 text-sm"
              onClick={() => {
                onChange({ id: u.id, label: userLabel(u) });
                setOpen(false);
              }}
            >
              <span className="text-zinc-200">{userLabel(u)}</span>
              <span className="text-zinc-600 text-xs"> · id {u.id} · tg {u.telegram_id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Reusable traffer bind/edit form (used inline in the detail modal). */
function TrafferEditor({ link }: { link: TrackingLink }) {
  const qc = useQueryClient();
  const [traffer, setTraffer] = useState<{ id: number; label: string } | null>(
    link.traffer_user_id
      ? {
          id: link.traffer_user_id,
          label: link.traffer_username ? `@${link.traffer_username}` : `#${link.traffer_user_id}`,
        }
      : null,
  );
  const [percent, setPercent] = useState(link.percent != null ? String(link.percent) : "");

  const save = useMutation({
    mutationFn: (body: { traffer_user_id: number | null; percent: number | null }) =>
      api(`/api/admin/tracking-links/${link.id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tracking-links"] }),
  });

  const pct = percent.trim() === "" ? null : Number(percent);
  const pctValid = pct === null || (pct >= 0 && pct <= 100);
  const canSave = traffer !== null && pct !== null && pctValid;

  return (
    <div className="space-y-3">
      <TrafferPicker value={traffer} onChange={setTraffer} />
      {traffer && (
        <div>
          <div className="text-xs text-zinc-500 mb-1">Процент выплаты с оплат (0–100)</div>
          <Input
            value={percent}
            placeholder="напр. 30"
            inputMode="decimal"
            onChange={(e) => setPercent(e.target.value.replace(/[^0-9.]/g, ""))}
          />
        </div>
      )}
      {!pctValid && <div className="text-xs text-red-400">Процент 0–100</div>}
      {save.isSuccess && <div className="text-xs text-emerald-400">Сохранено ✓</div>}
      {save.isError && <div className="text-xs text-red-400">{(save.error as Error).message}</div>}
      <div className="flex gap-3 justify-between">
        <Button
          variant="ghost"
          onClick={() => {
            setTraffer(null);
            setPercent("");
            save.mutate({ traffer_user_id: null, percent: null });
          }}
          disabled={save.isPending || !link.traffer_user_id}
        >
          Снять привязку
        </Button>
        <Button
          variant="primary"
          disabled={!canSave || save.isPending}
          onClick={() => save.mutate({ traffer_user_id: traffer!.id, percent: pct })}
        >
          {save.isPending ? "Сохраняем…" : "Сохранить"}
        </Button>
      </div>
    </div>
  );
}

function CreateModal({ open, onClose, botName }: { open: boolean; onClose: () => void; botName: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [traffer, setTraffer] = useState<{ id: number; label: string } | null>(null);
  const [percent, setPercent] = useState("");

  const reset = () => {
    setName("");
    setSlug("");
    setTraffer(null);
    setPercent("");
  };

  const create = useMutation({
    mutationFn: async () => {
      const link = await api<TrackingLink>("/api/admin/tracking-links", {
        method: "POST",
        body: JSON.stringify({ slug: slug.trim(), name: name.trim() }),
      });
      if (traffer && percent.trim() !== "") {
        await api(`/api/admin/tracking-links/${link.id}`, {
          method: "PATCH",
          body: JSON.stringify({ traffer_user_id: traffer.id, percent: Number(percent) }),
        });
      }
      return link;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tracking-links"] });
      reset();
      onClose();
    },
  });

  const pct = percent.trim() === "" ? null : Number(percent);
  const pctValid = pct === null || (pct >= 0 && pct <= 100);
  const trafferNeedsPct = traffer !== null && pct === null;

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Новая трекинг-ссылка"
    >
      <div className="space-y-4">
        <Field label="Название (видно только в админке)">
          <Input value={name} placeholder="Канал @durov" onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Slug (часть ссылки: a-z 0-9 _ -)">
          <Input value={slug} placeholder="durov_channel" onChange={(e) => setSlug(sanitizeSlug(e.target.value))} />
        </Field>
        {slug && (
          <div className="text-xs text-zinc-500 font-mono break-all bg-zinc-950/60 rounded-lg px-3 py-2">
            {trackingUrl(botName, slug)}
          </div>
        )}

        <div className="border-t border-zinc-800 pt-4 space-y-3">
          <div className="text-xs text-zinc-500">Траффер (необязательно — можно назначить позже)</div>
          <TrafferPicker value={traffer} onChange={setTraffer} />
          {traffer && (
            <Field label="Процент выплаты трафферу с оплат (0–100)">
              <Input
                value={percent}
                placeholder="напр. 30"
                inputMode="decimal"
                onChange={(e) => setPercent(e.target.value.replace(/[^0-9.]/g, ""))}
              />
            </Field>
          )}
          {trafferNeedsPct && (
            <div className="text-xs text-amber-400">Укажи процент, иначе трафферу ничего не начислится</div>
          )}
          {!pctValid && <div className="text-xs text-red-400">Процент 0–100</div>}
        </div>

        {create.isError && <div className="text-xs text-red-400">{(create.error as Error).message}</div>}
        <div className="flex gap-3 justify-end pt-2">
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!slug.trim() || !name.trim() || !pctValid || trafferNeedsPct || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Создаём…" : "Создать"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReferralStatsTab() {
  const botName = useBotUsername();
  const [limit] = useState(50);
  const [offset] = useState(0);

  const q = useQuery<ReferralStatsResponse>({
    queryKey: ["referral-stats", limit, offset],
    queryFn: () => api(`/api/admin/referral-stats?limit=${limit}&offset=${offset}`),
  });

  const [detailId, setDetailId] = useState<number | null>(null);
  const items = q.data?.items ?? [];
  const totals = items.reduce(
    (a, u) => {
      a.refs += u.total_refs;
      a.paid += u.paid_refs;
      a.revenue += u.revenue;
      a.earned += u.earned;
      return a;
    },
    { refs: 0, paid: 0, revenue: 0, earned: 0 },
  );
  const detail = items.find((u) => u.id === detailId) ?? null;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Рефереров" value={num.format(q.data?.total ?? 0)} hint="юзеров, кто кого-то привёл" accent="emerald" />
        <KpiCard label="Привлечено" value={num.format(totals.refs)} hint="всего приглашённых" />
        <KpiCard label="Купили" value={num.format(totals.paid)} hint="из приглашённых оплатили" />
        <KpiCard label="Выручка" value={rub0.format(totals.revenue)} hint={`₽ с рефералов · начислено рефереру ${rub0.format(totals.earned)}`} accent="emerald" />
      </div>

      <div className="text-zinc-400 text-sm mb-4">всего рефереров: {q.data?.total ?? 0}</div>

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {q.isError && <div className="text-red-400">Ошибка загрузки</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {items.map((u) => {
          const refLink = `https://t.me/${botName}?start=ref${u.telegram_id}`;
          const stop = (e: React.MouseEvent) => e.stopPropagation();
          return (
            <div
              key={u.id}
              onClick={() => setDetailId(u.id)}
              className="group rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex flex-col gap-3 cursor-pointer hover:border-zinc-600 hover:bg-zinc-900/70 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-zinc-100 font-medium truncate group-hover:text-brand transition-colors">
                    {userLabel(u)}
                  </div>
                  <div className="text-xs text-zinc-600 font-mono">id {u.id} · tg {u.telegram_id}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-600">баланс</div>
                  <div className="text-sm font-medium text-amber-400">{rub2.format(u.balance)}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2" onClick={stop}>
                <span className="font-mono text-xs text-zinc-400 truncate flex-1">{refLink}</span>
                <CopyField size="button-only" text={refLink} label="Копировать" />
              </div>

              <div className="grid grid-cols-4 sm:grid-cols-6 gap-y-3 gap-x-2 pt-1">
                <Stat label="Привёл" value={num.format(u.total_refs)} />
                <Stat label="Купили" value={num.format(u.paid_refs)} className="text-emerald-400" />
                <Stat label="Конв." value={`${u.conversion_pct}%`} className={convClass(u.conversion_pct)} />
                <Stat label="Актив 7д" value={num.format(u.active_7d)} />
                <Stat label="Выручка" value={rub0.format(u.revenue)} className="text-zinc-100" />
                <Stat label="Заработал" value={rub0.format(u.earned)} className="text-amber-400" />
              </div>
              <NewRenewal nc={u.new_count} nr={u.new_revenue} rc={u.renewal_count} rr={u.renewal_revenue} />

              <div className="text-xs text-zinc-600 group-hover:text-zinc-400 self-end">подробнее →</div>
            </div>
          );
        })}
      </div>

      {items.length === 0 && !q.isLoading && (
        <div className="text-zinc-500 text-sm mt-6 border border-dashed border-zinc-800 rounded-xl p-8 text-center">
          Рефералов пока нет
        </div>
      )}

      <RefDetailModal user={detail} botName={botName} onClose={() => setDetailId(null)} />
    </>
  );
}

function RefDetailModal({
  user,
  botName,
  onClose,
}: {
  user: ReferralUser | null;
  botName: string;
  onClose: () => void;
}) {
  const referred = useQuery<{ users: AttributedUser[] }>({
    queryKey: ["referral-referred", user?.id],
    queryFn: () => api(`/api/admin/referral-stats/${user!.id}/referrals`),
    enabled: !!user,
  });

  if (!user) return null;
  const refLink = `https://t.me/${botName}?start=ref${user.telegram_id}`;
  const list = referred.data?.users ?? [];

  return (
    <Modal open={user !== null} onClose={onClose} title={userLabel(user)} maxWidth="max-w-3xl">
      <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
        <div>
          <div className="text-xs text-zinc-500 mb-1">
            Реф-ссылка · id {user.id} · tg {user.telegram_id} · процент {user.percent ?? 0}%
          </div>
          <CopyField size="lg" text={refLink} />
        </div>

        <Card title="Воронка">
          <Funnel2 total={user.total_refs} paid={user.paid_refs} />
        </Card>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-y-4 gap-x-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <Stat label="Привёл" value={num.format(user.total_refs)} />
          <Stat label="Купили" value={num.format(user.paid_refs)} className="text-emerald-400" />
          <Stat label="Конверсия" value={`${user.conversion_pct}%`} className={convClass(user.conversion_pct)} />
          <Stat label="Активных 7д" value={num.format(user.active_7d)} />
          <Stat label="Оплат" value={num.format(user.payments)} />
          <Stat label="Средний чек" value={user.avg_check ? rub0.format(user.avg_check) : "—"} />
          <Stat label="Выручка" value={rub0.format(user.revenue)} className="text-zinc-100" />
          <Stat label="Заработал" value={rub0.format(user.earned)} className="text-amber-400" />
          <Stat label="Баланс" value={rub2.format(user.balance)} />
        </div>

        <Card title="Новые / продления">
          <NewRenewal nc={user.new_count} nr={user.new_revenue} rc={user.renewal_count} rr={user.renewal_revenue} />
        </Card>

        <Card title={`Кого привёл · ${list.length}`}>
          {referred.isLoading && <div className="text-zinc-500 text-sm">Загрузка…</div>}
          {!referred.isLoading && list.length === 0 && (
            <div className="text-zinc-500 text-sm">Пока никого не привёл.</div>
          )}
          {list.length > 0 && (
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>пользователь</Th>
                    <Th>пришёл</Th>
                    <Th>оплат</Th>
                    <Th>потратил</Th>
                    <Th>статус</Th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((u) => (
                    <tr key={u.id} className="hover:bg-zinc-900/50">
                      <Td>
                        <Link to={`/users/${u.id}`} className="text-brand hover:underline">
                          {userLabel(u)}
                        </Link>
                      </Td>
                      <Td className="text-zinc-400">{dt(u.created_at)}</Td>
                      <Td className="text-zinc-300">{u.payments}</Td>
                      <Td className="text-zinc-100">{u.spent ? rub0.format(u.spent) : "—"}</Td>
                      <Td>
                        {u.spent > 0 ? (
                          <span className="text-emerald-400 text-xs">платящий</span>
                        ) : (
                          <span className="text-zinc-600 text-xs">лид</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

        <div className="flex justify-between pt-1">
          <Link to={`/users/${user.id}`} className="text-sm text-brand hover:underline self-center">
            открыть профиль →
          </Link>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Funnel2({ total, paid }: { total: number; paid: number }) {
  const stages = [
    { label: "Привёл", value: total, color: "bg-sky-500" },
    { label: "Купили", value: paid, color: "bg-emerald-500" },
  ];
  const max = Math.max(total, 1);
  return (
    <div className="space-y-3">
      {stages.map((s, i) => {
        const prev = i > 0 ? stages[i - 1].value : null;
        const dropPct = prev && prev > 0 ? Math.round((s.value / prev) * 100) : null;
        const width = Math.round((s.value / max) * 100);
        return (
          <div key={s.label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-zinc-400">{s.label}</span>
              <span className="text-zinc-200">
                {num.format(s.value)}
                {dropPct != null && <span className="text-zinc-500"> · {dropPct}% ↓</span>}
              </span>
            </div>
            <div className="h-2.5 rounded bg-zinc-800 overflow-hidden">
              <div className={`h-full ${s.color}`} style={{ width: `${Math.max(width, 2)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: "emerald";
}) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3">
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      <div className={`text-xl font-semibold ${accent === "emerald" ? "text-emerald-400" : "text-zinc-100"}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-4 rounded-lg text-sm border transition-colors ${
        active
          ? "bg-brand-soft text-brand glow-ring border-transparent"
          : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
